import { authService } from '@/features/auth';
import type { User, LoginResult } from '@/features/auth';
import * as Sentry from '@sentry/react';
import { ReactNode, useEffect, useState } from 'react';
import { AuthContext } from './auth-context';
import { isAxiosError } from 'axios';
import { Button } from '@/design-system';
import { logger } from '@/core/logger/logger';
import { queryClient } from '@/core/data-fetching/queryClient';
import { SESSION_EXPIRED_EVENT } from '@/core/api/session-policy';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [validationFailed, setValidationFailed] = useState(false);
  const [validationAttempt, setValidationAttempt] = useState(0);

  useEffect(() => {
    let canceled = false;
    const initAuth = async () => {
      setValidationFailed(false);
      if (authService.isAuthenticated()) {
        const token = authService.getToken();
        try {
          const userData = await authService.getMe();
          if (!canceled && token === authService.getToken()) setUser(userData);
        } catch (error) {
          if (!canceled && token === authService.getToken()) {
            if (isAxiosError(error) && error.response?.status === 401) {
              authService.logout();
              setUser(null);
              queryClient.clear();
            } else {
              const cachedUser = authService.getUser();
              setUser(cachedUser);
              setValidationFailed(!cachedUser);
              logger.warn('[auth] Profile unavailable; preserving session');
            }
          }
        }
      }
      if (!canceled) setIsLoading(false);
    };

    void initAuth();
    return () => { canceled = true; };
  }, [validationAttempt]);

  useEffect(() => {
    const expired = () => { setUser(null); queryClient.clear(); };
    window.addEventListener(SESSION_EXPIRED_EVENT, expired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, expired);
  }, []);

  /**
   * Sincroniza identidade no Sentry sempre que `user` muda. Em produção
   * NÃO mandamos email (PII — busca por userId resolve 99% dos casos).
   */
  useEffect(() => {
    if (!user) {
      Sentry.setUser(null);
      return;
    }
    const isProd = import.meta.env.PROD;
    Sentry.setUser(
      isProd ? { id: user.id } : { id: user.id, email: user.email },
    );
    Sentry.setTag('role', user.role);
  }, [user]);

  const login = async (email: string, password: string): Promise<LoginResult> => {
    const result = await authService.login({ email, password });
    // Só emite sessão local quando não há desafio/enrollment 2FA pendente.
    if (!result.requires2fa && !result.requires2faSetup) {
      setUser(result.user);
    }
    return result;
  };

  /** Completa o login após o desafio 2FA. */
  const completeMfa = async (tmpToken: string, code: string) => {
    const u = await authService.verify2fa(tmpToken, code);
    setUser(u);
  };

  /** Enrollment obrigatório: confirma o 2FA no login e emite a sessão. */
  const completeMfaEnroll = async (tmpToken: string, code: string): Promise<string[]> => {
    const { user: u, backupCodes } = await authService.enroll2faConfirm(tmpToken, code);
    setUser(u);
    return backupCodes;
  };

  const logout = () => {
    authService.logout();
    setUser(null);
    queryClient.clear();
  };

  const refreshUser = async () => {
    if (authService.isAuthenticated()) {
      const token = authService.getToken();
      try {
        const userData = await authService.getMe();
        if (token === authService.getToken()) setUser(userData);
      } catch (error) {
        if (token !== authService.getToken()) return;
        if (isAxiosError(error) && error.response?.status === 401) logout();
        else logger.warn('[auth] Profile refresh failed; preserving session');
      }
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        completeMfa,
        completeMfaEnroll,
        logout,
        refreshUser,
      }}
    >
      {validationFailed && !user ? (
        <main className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
          <section role="alert" className="space-y-4 rounded-xl border border-gray-200 bg-white p-6 text-sm">
            <p>Não foi possível verificar sua sessão. Tente novamente sem precisar entrar de novo.</p>
            <Button size="sm" variant="subtle" disabled={isLoading} onClick={() => { setIsLoading(true); setValidationAttempt((attempt) => attempt + 1); }}>Tentar novamente</Button>
          </section>
        </main>
      ) : children}
    </AuthContext.Provider>
  );
}
